import { useCallback, useEffect, useState } from 'react'

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000

export type CountdownParts = {
  hours: string
  minutes: string
  seconds: string
}

type UseCountdownOptions = {
  /** When false, no interval is created — used to keep the timer off the critical path. */
  enabled?: boolean
  durationMs?: number
}

type UseCountdownResult = {
  parts: CountdownParts
  isFinished: boolean
  restart: () => void
}

function pad(value: number): string {
  return value.toString().padStart(2, '0')
}

/**
 * 24h countdown. Resets itself automatically when it reaches zero,
 * and can be restarted manually via `restart()`.
 * The ticking interval only starts once `enabled` is true.
 */
export function useCountdown({
  enabled = true,
  durationMs = TWENTY_FOUR_HOURS,
}: UseCountdownOptions = {}): UseCountdownResult {
  const [deadline, setDeadline] = useState<number>(() => Date.now() + durationMs)
  const [remaining, setRemaining] = useState<number>(durationMs)

  useEffect(() => {
    if (!enabled) return

    const tick = () => {
      const diff = deadline - Date.now()
      if (diff <= 0) {
        setDeadline(Date.now() + durationMs)
        setRemaining(durationMs)
        return
      }
      setRemaining(diff)
    }

    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [deadline, durationMs, enabled])

  const restart = useCallback(() => {
    setDeadline(Date.now() + durationMs)
    setRemaining(durationMs)
  }, [durationMs])

  const totalSeconds = Math.max(0, Math.floor(remaining / 1000))

  return {
    parts: {
      hours: pad(Math.floor(totalSeconds / 3600)),
      minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
      seconds: pad(totalSeconds % 60),
    },
    isFinished: enabled && totalSeconds === 0,
    restart,
  }
}
